import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ExternalLink, Receipt } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatDateTime, formatMoney } from '../lib/format';
import { INVOICE_STATUS_LABEL, INVOICE_STATUS_TONE, type DamageInvoiceSummary } from '../lib/incidents';
import type { BlackbaudBillingIntegration, Page } from '../lib/types';
import { useIntegrationMutations } from './integration-shell';
import { Alert, Badge, Button, EmptyState, Field, Input, Pagination, Spinner, Table, Td, Th } from './ui';

const FEE_TYPE = /^\d{1,10}$/;

/** How Billing Management is connected and set up: the Blackbaud connection it uses, and the fee type damage goes under. */
export function BlackbaudBillingSetup({ integration }: { integration: BlackbaudBillingIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const { update } = useIntegrationMutations('BLACKBAUD_BILLING');
  const [feeTypeId, setFeeTypeId] = useState('');
  const blackbaud = integration.billing.blackbaud;

  useEffect(() => {
    setFeeTypeId(integration.settings.feeTypeId ? String(integration.settings.feeTypeId) : '');
  }, [integration.settings.feeTypeId]);

  const trimmed = feeTypeId.trim();
  const valid = !trimmed || FEE_TYPE.test(trimmed);

  return (
    <div className="max-w-2xl space-y-8">
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">1. Blackbaud connection</h3>
          <p className="text-sm text-slate-500">Billing Management uses the connection from the Blackbaud SKY API integration. There’s nothing else to sign in to.</p>
        </div>
        {blackbaud.mode === 'DEMO' ? (
          <Alert tone="blue" title="Blackbaud is in demo mode">
            Posting an invoice records a sample import without billing anyone.
          </Alert>
        ) : blackbaud.connected ? (
          <Alert tone={blackbaud.status === 'ERROR' ? 'amber' : 'green'} title={`Connected${blackbaud.environmentName ? ` to ${blackbaud.environmentName}` : ''}`}>
            {blackbaud.status === 'ERROR' ? 'The Blackbaud connection needs attention before invoices can be posted.' : 'Invoices post through this connection.'}
          </Alert>
        ) : (
          <Alert tone="amber" title="Blackbaud isn’t connected">
            Connect Blackbaud first.
          </Alert>
        )}
        <Link to="/integrations/blackbaud" className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline">
          Blackbaud SKY API <ExternalLink className="size-3.5" />
        </Link>
      </section>

      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">2. Fee type</h3>
          <p className="text-sm text-slate-500">The Billing Management fee type damage charges go under. Every charge needs one.</p>
        </div>
        <Field label="Fee type ID" error={valid ? undefined : 'Enter the fee type’s number'}>
          <Input
            value={feeTypeId}
            onChange={(event) => setFeeTypeId(event.target.value)}
            inputMode="numeric"
            className="w-40 font-mono"
            placeholder="e.g. 124"
            disabled={!isAdmin}
          />
        </Field>
        {isAdmin ? (
          <Button
            variant="primary"
            loading={update.isPending}
            disabled={!valid || trimmed === String(integration.settings.feeTypeId ?? '')}
            onClick={() => update.mutate({ settings: { feeTypeId: trimmed ? Number(trimmed) : null } })}
          >
            Save
          </Button>
        ) : (
          <Alert tone="blue">Only administrators can change Billing Management.</Alert>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-900">3. Turn it on</h3>
        <p className="text-sm text-slate-500">
          Turn on <strong>Enabled</strong> above. Then an administrator chooses <strong>Post to Blackbaud</strong> on each invoice, under Incidents &amp; billing.
        </p>
        {integration.enabled && !integration.settings.feeTypeId && <Alert tone="amber">Enter the fee type before posting invoices.</Alert>}
      </section>

      <p className="border-t border-slate-100 pt-5 text-sm text-slate-600">
        The Blackbaud account that connected Edvora needs access to Billing Management’s batch imports. Only students synced from Blackbaud can be billed there,
        and voiding an invoice in Edvora doesn’t remove its charge from Blackbaud. Invoice due dates and payment instructions are under{' '}
        <Link to="/settings?tab=billing" className="font-medium text-brand-700 hover:underline">
          Settings → Damage billing
        </Link>
        .
      </p>
    </div>
  );
}

/** Invoices posted to Billing Management, most recent first. */
export function BlackbaudPostedInvoices() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: ['invoices', 'blackbaud', page],
    queryFn: () => api.get<Page<DamageInvoiceSummary>>('/invoices', { blackbaud: 'posted', page, pageSize: 25 }),
    placeholderData: keepPreviousData,
  });

  if (query.isLoading) return <Spinner />;
  if (query.error || !query.data) return <Alert tone="red">Couldn’t load posted invoices.</Alert>;
  if (query.data.items.length === 0) {
    return <EmptyState icon={<Receipt />} title="Nothing posted yet" description="Invoices posted to Blackbaud show up here." />;
  }
  return (
    <div className="-mx-5 -mb-5">
      <Table>
        <thead>
          <tr>
            <Th>Invoice</Th>
            <Th>Student</Th>
            <Th className="text-right">Total</Th>
            <Th>Status</Th>
            <Th>Posted</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {query.data.items.map((invoice) => (
            <tr key={invoice.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/invoices/${invoice.id}`)}>
              <Td className="whitespace-nowrap">
                <Link to={`/invoices/${invoice.id}`} onClick={(event) => event.stopPropagation()} className="font-medium text-slate-900 hover:text-brand-700">
                  #{invoice.number}
                </Link>
                <p className="text-xs text-slate-500">{invoice.incident.asset.assetTag}</p>
              </Td>
              <Td>{invoice.studentName}</Td>
              <Td className="text-right font-medium whitespace-nowrap">{formatMoney(invoice.total)}</Td>
              <Td>
                <Badge tone={INVOICE_STATUS_TONE[invoice.status]}>{INVOICE_STATUS_LABEL[invoice.status]}</Badge>
              </Td>
              <Td className="whitespace-nowrap text-slate-600">
                <span title={formatDateTime(invoice.blackbaudPostedAt)}>{formatDate(invoice.blackbaudPostedAt)}</span>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {query.data.pageCount > 1 && <Pagination page={page} pageSize={query.data.pageSize} total={query.data.total} onPage={setPage} />}
    </div>
  );
}
